/**
 * Toast notification system for displaying user errors and feedback.
 * Auto-dismisses after a configurable timeout.
 * Styled for different severity levels.
 */

import { memo, useEffect, useState, useCallback } from 'react'
import { X, AlertCircle, AlertTriangle, Info, CheckCircle } from 'lucide-react'
import { cn } from '../../lib/utils'

// =============================================================================
// Types
// =============================================================================

export type ToastSeverity = 'error' | 'warning' | 'info' | 'success'

export interface ToastData {
  /** Unique ID for the toast */
  id: string
  /** Title of the notification */
  title: string
  /** Detailed message */
  message: string
  /** Severity level for styling */
  severity: ToastSeverity
  /** Optional action button text */
  actionText?: string
  /** Optional action callback */
  onAction?: () => void
}

export interface ToastProps {
  toast: ToastData
  /** Called when toast should be dismissed */
  onDismiss: (id: string) => void
  /** Auto-dismiss timeout in ms (0 to disable) */
  timeout?: number
}

// =============================================================================
// Toast Component
// =============================================================================

const severityConfig: Record<
  ToastSeverity,
  { icon: typeof AlertCircle; bgClass: string; borderClass: string; iconClass: string }
> = {
  error: {
    icon: AlertCircle,
    bgClass: 'bg-red-50 dark:bg-red-950/50',
    borderClass: 'border-red-200 dark:border-red-800',
    iconClass: 'text-red-500',
  },
  warning: {
    icon: AlertTriangle,
    bgClass: 'bg-amber-50 dark:bg-amber-950/50',
    borderClass: 'border-amber-200 dark:border-amber-800',
    iconClass: 'text-amber-500',
  },
  info: {
    icon: Info,
    bgClass: 'bg-blue-50 dark:bg-blue-950/50',
    borderClass: 'border-blue-200 dark:border-blue-800',
    iconClass: 'text-blue-500',
  },
  success: {
    icon: CheckCircle,
    bgClass: 'bg-green-50 dark:bg-green-950/50',
    borderClass: 'border-green-200 dark:border-green-800',
    iconClass: 'text-green-500',
  },
}

/**
 * Single toast notification.
 */
export const Toast = memo(function Toast({
  toast,
  onDismiss,
  timeout = 5000,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)

  const config = severityConfig[toast.severity]
  const Icon = config.icon

  const handleDismiss = useCallback(() => {
    setIsLeaving(true)
    // Wait for exit animation
    setTimeout(() => {
      onDismiss(toast.id)
    }, 200)
  }, [onDismiss, toast.id])

  // Entry animation
  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true)
    })
  }, [])

  // Auto-dismiss
  useEffect(() => {
    if (timeout <= 0) return

    const timer = setTimeout(handleDismiss, timeout)
    return () => clearTimeout(timer)
  }, [timeout, handleDismiss])

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border shadow-lg transition-all duration-200',
        config.bgClass,
        config.borderClass,
        isVisible && !isLeaving
          ? 'translate-y-0 opacity-100'
          : 'translate-y-2 opacity-0'
      )}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <Icon className={cn('h-5 w-5 flex-shrink-0', config.iconClass)} aria-hidden="true" />

          {/* Content */}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">{toast.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{toast.message}</p>

            {/* Action button */}
            {toast.actionText && toast.onAction && (
              <button
                type="button"
                onClick={() => {
                  toast.onAction?.()
                  handleDismiss()
                }}
                className="mt-2 text-sm font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                {toast.actionText}
              </button>
            )}
          </div>

          {/* Dismiss button */}
          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 rounded p-1 text-muted-foreground hover:bg-muted/50 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Progress bar for auto-dismiss */}
      {timeout > 0 && (
        <div className="h-1 w-full overflow-hidden bg-muted/30">
          <div
            className={cn(
              'h-full transition-all ease-linear',
              toast.severity === 'error'
                ? 'bg-red-500'
                : toast.severity === 'warning'
                  ? 'bg-amber-500'
                  : toast.severity === 'info'
                    ? 'bg-blue-500'
                    : 'bg-green-500'
            )}
            style={{
              width: '100%',
              animation: `shrink ${timeout}ms linear forwards`,
            }}
          />
        </div>
      )}

      {/* CSS for progress bar animation */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  )
})

// =============================================================================
// Toast Container
// =============================================================================

export interface ToastContainerProps {
  /** Array of active toasts */
  toasts: ToastData[]
  /** Called to dismiss a toast */
  onDismiss: (id: string) => void
  /** Position on screen */
  position?: 'top-right' | 'top-center' | 'bottom-right' | 'bottom-center'
  /** Auto-dismiss timeout in ms */
  timeout?: number
}

const positionClasses: Record<NonNullable<ToastContainerProps['position']>, string> = {
  'top-right': 'top-4 right-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-4 right-4',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
}

/**
 * Container for managing multiple toasts.
 */
export const ToastContainer = memo(function ToastContainer({
  toasts,
  onDismiss,
  position = 'top-right',
  timeout = 5000,
}: ToastContainerProps) {
  if (toasts.length === 0) {
    return null
  }

  return (
    <div
      className={cn(
        'pointer-events-none fixed z-50 flex flex-col gap-2',
        positionClasses[position]
      )}
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          toast={toast}
          onDismiss={onDismiss}
          timeout={timeout}
        />
      ))}
    </div>
  )
})
